import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Edit, Save } from 'lucide-react';

interface SiteContent {
  id: string;
  section: string;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  metadata: any;
  is_active: boolean;
}

export const ContentManagement = () => {
  const [contents, setContents] = useState<SiteContent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingContent, setEditingContent] = useState<SiteContent | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchContents = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('site_content')
        .select('*')
        .order('section');

      if (error) throw error;
      setContents(data || []);
    } catch (error) {
      console.error('Error fetching site content:', error);
      toast.error('Fout bij laden van content');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContents();
  }, []);

  const handleEdit = (content: SiteContent) => {
    setEditingContent(content);
    setDialogOpen(true);
  };

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingContent) return;

    const formData = new FormData(e.currentTarget);
    
    try {
      const { error } = await supabase
        .from('site_content')
        .update({
          title: formData.get('title') as string,
          subtitle: formData.get('subtitle') as string,
          description: formData.get('description') as string,
          is_active: editingContent.is_active,
        })
        .eq('id', editingContent.id);

      if (error) throw error;

      toast.success('Content succesvol bijgewerkt');
      setDialogOpen(false);
      fetchContents();
    } catch (error) {
      console.error('Error updating content:', error);
      toast.error('Fout bij bijwerken van content');
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Site Content Beheer</h2>
      </div>

      <div className="grid gap-4">
        {contents.map((content) => (
          <Card key={content.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="capitalize">{content.section}</CardTitle>
                  <CardDescription>{content.title}</CardDescription>
                </div>
                <Button onClick={() => handleEdit(content)} variant="outline" size="sm">
                  <Edit className="w-4 h-4 mr-2" />
                  Bewerken
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                {content.subtitle && (
                  <p className="text-muted-foreground">
                    <span className="font-semibold">Subtitle:</span> {content.subtitle}
                  </p>
                )}
                {content.description && (
                  <p className="text-muted-foreground">
                    <span className="font-semibold">Beschrijving:</span> {content.description}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <span className="font-semibold">Status:</span>
                  <span className={content.is_active ? 'text-green-600' : 'text-red-600'}>
                    {content.is_active ? 'Actief' : 'Inactief'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Content Bewerken: {editingContent?.section}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titel</Label>
              <Input
                id="title"
                name="title"
                defaultValue={editingContent?.title || ''}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subtitle">Subtitle</Label>
              <Input
                id="subtitle"
                name="subtitle"
                defaultValue={editingContent?.subtitle || ''}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Beschrijving</Label>
              <Textarea
                id="description"
                name="description"
                rows={4}
                defaultValue={editingContent?.description || ''}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={editingContent?.is_active}
                onCheckedChange={(checked) =>
                  setEditingContent(prev => prev ? { ...prev, is_active: checked } : null)
                }
              />
              <Label htmlFor="is_active">Actief</Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuleren
              </Button>
              <Button type="submit">
                <Save className="w-4 h-4 mr-2" />
                Opslaan
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
